import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, PlusSquare, Images, Inbox, MailCheck, Settings, ExternalLink, LogOut, Menu, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "../ui/sheet";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

/** Every area of the admin, in one place. Used by the header, the mobile menu and the dashboard cards. */
export const ADMIN_AREAS = [
  { id: "dashboard", to: "/admin", label: "Dashboard", icon: LayoutDashboard, blurb: "Traffic at a glance and your most recent promo pages.", isActive: (p) => p === "/admin" },
  {
    id: "pages",
    to: "/admin/pages",
    label: "Landing Pages",
    icon: FileText,
    blurb: "Every promo page: edit, publish, duplicate, copy links and open stats.",
    isActive: (p) => p.startsWith("/admin/pages") && p !== "/admin/pages/new",
  },
  { id: "new", to: "/admin/pages/new", label: "New Page", icon: PlusSquare, blurb: "Start a fresh landing page for your next campaign.", isActive: (p) => p === "/admin/pages/new", inNav: false },
  { id: "media", to: "/admin/media", label: "Media Library", short: "Media", icon: Images, blurb: "Upload and manage photos used on your landing pages.", isActive: (p) => p.startsWith("/admin/media") },
  {
    id: "submissions",
    to: "/admin/submissions",
    label: "Submissions",
    icon: Inbox,
    blurb: "Every guest-spot request, promo lead and contact message, with email delivery status.",
    isActive: (p) => p.startsWith("/admin/submissions"),
  },
  {
    id: "forms",
    to: "/admin/forms",
    label: "Forms & Email",
    icon: MailCheck,
    blurb: "Choose who receives each form and edit the automatic follow-up emails.",
    isActive: (p) => p.startsWith("/admin/forms"),
  },
  { id: "settings", to: "/admin/settings", label: "Settings", icon: Settings, blurb: "Your account, team access for Google sign-in and password.", isActive: (p) => p.startsWith("/admin/settings") },
];

const NAV_AREAS = ADMIN_AREAS.filter((a) => a.inNav !== false);

const Brand = () => (
  <Link to="/admin" className="group flex items-center gap-3" data-testid="admin-brand">
    <span className="flex h-9 w-9 items-center justify-center border border-foreground/30 transition-colors group-hover:border-primary">
      <span className="display text-2xl leading-none">A</span>
    </span>
    <span className="flex flex-col leading-none">
      <span className="display text-xl tracking-wide">AVALON</span>
      <span className="text-[9px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">Admin</span>
    </span>
  </Link>
);

const AdminHeader = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl" data-testid="admin-header">
      <div className="mx-auto flex h-[68px] w-full max-w-[1600px] items-center justify-between gap-6 px-5 lg:px-8">
        <Brand />

        {/* Desktop nav: labelled links to every area */}
        <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Admin areas">
          {NAV_AREAS.map((area) => {
            const active = area.isActive(pathname);
            return (
              <NavLink
                key={area.id}
                to={area.to}
                className={cn(
                  "flex items-center gap-2 border-b-2 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors",
                  active ? "border-primary text-primary" : "border-transparent text-foreground/75 hover:text-foreground"
                )}
                aria-current={active ? "page" : undefined}
                data-testid={`admin-nav-${area.id}`}
              >
                <area.icon className="h-4 w-4" /> {area.short || area.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground" data-testid="admin-view-site">
            <ExternalLink className="h-4 w-4" /> <span className="hidden xl:inline">View site</span>
          </a>
          <span className="h-5 w-px bg-border" />
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {user?.picture && <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-border" referrerPolicy="no-referrer" />}
            <span className="max-w-[160px] truncate font-semibold text-foreground" data-testid="admin-username">{user?.name || user?.username}</span>
          </span>
          <Button variant="outline" size="sm" className="h-9 rounded-none" onClick={logout} data-testid="admin-logout">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>

        {/* Mobile / tablet menu with full labels */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 rounded-none lg:hidden" aria-label="Open admin menu" data-testid="admin-menu-trigger">
              <Menu className="mr-2 h-4 w-4" /> Menu
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88vw] max-w-sm border-border bg-background p-0">
            <SheetTitle className="sr-only">Admin menu</SheetTitle>
            <SheetDescription className="sr-only">Links to every area of the admin</SheetDescription>
            <div className="flex h-full flex-col">
              <div className="border-b border-border px-6 py-5">
                <Brand />
              </div>
              <nav className="flex-1 px-4 py-4" aria-label="Admin areas">
                {ADMIN_AREAS.map((area) => {
                  const active = area.isActive(pathname);
                  return (
                    <SheetClose asChild key={area.id}>
                      <NavLink
                        to={area.to}
                        className={cn("flex items-center justify-between gap-3 border-b border-border/60 px-2 py-4 text-sm font-semibold transition-colors", active ? "text-primary" : "text-foreground hover:text-primary")}
                        data-testid={`admin-mobile-nav-${area.id}`}
                      >
                        <span className="flex items-center gap-3">
                          <area.icon className="h-4 w-4" /> {area.label}
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </NavLink>
                    </SheetClose>
                  );
                })}
              </nav>
              <div className="space-y-3 border-t border-border px-6 py-5">
                <p className="text-xs text-muted-foreground">
                  Signed in as <span className="font-semibold text-foreground">{user?.name || user?.username}</span>
                </p>
                <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-semibold hover:text-primary">
                  <ExternalLink className="h-4 w-4" /> View public site
                </a>
                <SheetClose asChild>
                  <Button variant="outline" className="w-full rounded-none" onClick={logout} data-testid="admin-mobile-logout">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default AdminHeader;
